
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { Alert, Text } from 'braid-design-system';
import { InlineCode, SmartTextLink } from 'scoobie';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "profile-purchases"
    }}>{`Profile purchases`}</h1>
    <p>{`A candidate’s SEEK Profile may be purchased by a hirer in `}<a parentName="p" {...{
        "href": "https://talent.seek.com.au/partners/integrations/talent-search-connect/"
      }}>{`Talent Search`}</a>{`.
This allows the hirer to view the profile on the SEEK platform,
but also makes it available for export into your recruitment software.`}</p>
    <p>{`A `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/CandidateProfilePurchasedEvent"
      }}><inlineCode parentName="a">{`CandidateProfilePurchased`}</inlineCode>{` event`}</a>{` is emitted when a hirer purchases a candidate profile in Talent Search.
Create a `}<a parentName="p" {...{
        "href": "/events/webhooks#subscription-management"
      }}>{`webhook subscription`}</a>{` to trigger an export workflow when these events occur:`}</p>
    <img alt="" data-scoobie-style="none" src={require('../../../../mermaid/.295ca11ad304fe571d2f694776ed36543e572493.mmd.svg')} title="Webhook interaction" />
    <p>{`Your software should display the candidate profile and resume attachment to the position’s hirer `}<a parentName="p" {...{
        "href": "https://talent.seek.com.au/partners/terms-of-use/#talent-search-connect"
      }}>{`within 60 seconds`}</a>{` of receiving a purchase event.`}</p>
    <h2 {...{
      "id": "data-structure"
    }}>{`Data structure`}</h2>
    <p>{`A webhook body containing a `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/CandidateProfilePurchasedEvent"
      }}><inlineCode parentName="a">{`CandidateProfilePurchased`}</inlineCode>{` event`}</a>{` looks like so:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsonc",
        "metastring": "sample-webhook-body",
        "sample-webhook-body": true
      }}>{`{
  "events": [
    {
      "id": "seekAnzPublicTest:event:events:N3P1rAeTfSy8UpE5p984Hd",
      "type": "CandidateProfilePurchased",

      "createDateTime": "2020-08-07T13:56:38.385Z",

      "candidateProfileId": "seekAnzPublicTest:candidateProfile:purchased:HWxJyVNyio4JLWLmDY6eqV",

      // This is only available for signed webhook subscriptions
      "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h"
    }
  ],
  "subscriptionId": "seekAnzPublicTest:webhookSubscription:events:JurnLNCzY3T8wYRVLFWQNw",
  "url": "https://example.com/webhook/candidate-profile-purchased"
}
`}</code></pre>

    <Alert tone="info" mdxType="Alert">
  <Text mdxType="Text">
    The <InlineCode mdxType="InlineCode">candidateProfileId</InlineCode> provided within the webhook
    body is specific to that purchase event and cannot be used as a permanent
    stable identifier for the candidate. The
    <InlineCode mdxType="InlineCode">candidateProfileId</InlineCode> remains valid for retrieval for
    a period of up to 90 days. Attempts to retrieve a{' '}
    <SmartTextLink href="https://developer.seek.com/schema/#/named-type/CandidateProfile" mdxType="SmartTextLink">
      CandidateProfile
    </SmartTextLink>{' '}
    using a <InlineCode mdxType="InlineCode">candidateProfileId</InlineCode> older than 90 days will
    result in a null response. This may also occur if the candidate has deleted
    their SEEK Profile.
  </Text>
    </Alert>
    <p>{`A `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/CandidateProfile"
      }}><inlineCode parentName="a">{`CandidateProfile`}</inlineCode></a>{` from Talent Search contains candidate information,
a URL to the candidate’s resume file,
the organization making the purchase,
and the position that the profile should be exported to in your software.`}</p>
    <p>{`It contains the following information from the candidate’s SEEK Profile:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Candidate name`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Email address`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Phone number (if provided)`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Information about the candidate’s current employment (if applicable),
including employer name,
role title,
start date and description.`}</p>
      </li>
    </ul>
    <p>{`This information can be used to create a new candidate profile,
match on and update an existing candidate profile,
and help a hirer identify the candidate in your software.`}</p>
    <p>{`When performing matching activities (e.g. determining if a new candidate record for the purchased profile needs to be created in your software),
using the candidate’s email address as the unique identifier is recommended.`}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/PositionProfile/field/positionOrganizations"
      }}><inlineCode parentName="a">{`positionOrganizations.id`}</inlineCode></a>{` and `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/PositionProfile/field/profileId"
      }}><inlineCode parentName="a">{`positionProfiles.profileId`}</inlineCode></a>{` values should be used to identify the corresponding position in your software that the purchased profile should be added to.`}</p>
    <p>{`The SEEK API does not translate user input and will serve SEEK Profile information as supplied by the candidate.
As a result, your software may need to handle multilingual candidate data.`}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/CandidateProfile"
      }}><inlineCode parentName="a">{`CandidateProfile`}</inlineCode></a>{` object and operations are shared with the Optimised Apply use case.
See the documentation on `}<a parentName="p" {...{
        "href": "/use-cases/application-export/candidate-profiles"
      }}>{`exporting candidate profiles`}</a>{` and `}<a parentName="p" {...{
        "href": "/use-cases/application-export/attachments"
      }}>{`exporting attachments`}</a>{` for more information.`}</p>
    <h2 {...{
      "id": "operations"
    }}>{`Operations`}</h2>
    <h3 {...{
      "id": "retrieve-a-purchased-candidate-profile"
    }}>{`Retrieve a purchased candidate profile`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/candidateProfile"
      }}><inlineCode parentName="a">{`candidateProfile`}</inlineCode>{` query`}</a>{` accepts the event `}<inlineCode parentName="p">{`candidateProfileId`}</inlineCode>{` as input:`}</p>
    <pre><code parentName="pre" {...{
        "4567": true,
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($id: String!) {\\n  candidateProfile(id: $id) {\\n    candidate {\\n      person {\\n        name {\\n          given\\n          family\\n        }\\n        communication {\\n          phone {\\n            formattedNumber\\n          }\\n          email {\\n            address\\n          }\\n        }\\n      }\\n    }\\n    associatedPositionProfile {\\n      profileId {\\n        value\\n      }\\n      positionOrganizations {\\n        id {\\n          value\\n        }\\n        name\\n      }\\n    }\\n    employment {\\n      organization {\\n        name\\n      }\\n      positionHistories {\\n        start\\n        end\\n        title\\n        descriptions\\n        current\\n      }\\n    }\\n    attachments {\\n      seekRoleCode\\n      url\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":111,\"column\":1,\"offset\":4474},\"end\":{\"line\":159,\"column\":4,\"offset\":5189},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"id\\\": \\\"seekAnzPublicTest:candidateProfile:purchased:9krTD8euJ7Cxszcf\\\"\\n}\",\"position\":{\"start\":{\"line\":161,\"column\":1,\"offset\":5191},\"end\":{\"line\":165,\"column\":4,\"offset\":5278},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n  \\\"candidateProfile\\\": {\\n    // Name and contact details\\n    \\\"candidate\\\": {\\n      \\\"person\\\": {\\n        \\\"name\\\": {\\n          \\\"given\\\": \\\"Alexander\\\",\\n          \\\"family\\\": \\\"Watsford\\\"\\n        },\\n        \\\"communication\\\": {\\n          \\\"phone\\\": [\\n            {\\n              \\\"formattedNumber\\\": \\\"(07) 4567 1540\\\"\\n            }\\n          ],\\n          \\\"email\\\": [\\n            {\\n              \\\"address\\\": \\\"alexander.watsford@gmail.com\\\"\\n            }\\n          ]\\n        }\\n      }\\n    },\\n    \\\"associatedPositionProfile\\\": {\\n      \\\"profileId\\\": {\\n        \\\"value\\\": \\\"globalPublicTest:positionProfile:unposted:GSg8dZtux2FD8hP4Bmju3B\\\"\\n      },\\n      // The hirer associated with the unposted position profile\\n      \\\"positionOrganizations\\\": [\\n        {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n          },\\n          \\\"name\\\": \\\"Acme Corp\\\"\\n        }\\n      ]\\n    },\\n    // Current employment information from the candidate’s SEEK Profile\\n    \\\"employment\\\": [\\n      {\\n        \\\"organization\\\": {\\n          \\\"name\\\": \\\"Sofa Express\\\"\\n        },\\n        \\\"positionHistories\\\": [\\n          {\\n            \\\"start\\\": \\\"2020-06-10T04:58:23.730Z\\\",\\n            \\\"end\\\": null,\\n            \\\"title\\\": \\\"Store manager\\\",\\n            \\\"descriptions\\\": [],\\n            \\\"current\\\": true\\n          }\\n        ]\\n      }\\n    ],\\n    \\\"attachments\\\": [\\n      {\\n        // `Resume` document\\n        \\\"seekRoleCode\\\": \\\"Resume\\\",\\n        \\\"url\\\": \\\"https://graphql.seek.com/anzPublicTest/purchasedProfiles/seekAnzPublicTest%3AcandidateProfile%3Apurchased%3A73nRti9c7jBm/attachments/resume\\\"\\n      }\\n    ]\\n  }\\n}\",\"position\":{\"start\":{\"line\":167,\"column\":1,\"offset\":5280},\"end\":{\"line\":231,\"column\":4,\"offset\":6862},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($id:": true,
        "String!)": true,
        "{\\n": true,
        "": true,
        "candidateProfile(id:": true,
        "$id)": true,
        "candidate": true,
        "person": true,
        "name": true,
        "given\\n": true,
        "family\\n": true,
        "}\\n": true,
        "communication": true,
        "phone": true,
        "formattedNumber\\n": true,
        "email": true,
        "address\\n": true,
        "associatedPositionProfile": true,
        "profileId": true,
        "value\\n": true,
        "positionOrganizations": true,
        "id": true,
        "name\\n": true,
        "employment": true,
        "organization": true,
        "positionHistories": true,
        "start\\n": true,
        "end\\n": true,
        "title\\n": true,
        "descriptions\\n": true,
        "current\\n": true,
        "attachments": true,
        "seekRoleCode\\n": true,
        "url\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":111,\"column\":1,\"offset\":4474},\"end\":{\"line\":159,\"column\":4,\"offset\":5189},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:candidateProfile:purchased:9krTD8euJ7Cxszcf\\\"\\n}\",\"position\":{\"start\":{\"line\":161,\"column\":1,\"offset\":5191},\"end\":{\"line\":165,\"column\":4,\"offset\":5278},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"candidateProfile\\\":": true,
        "//": true,
        "Name": true,
        "and": true,
        "contact": true,
        "details\\n": true,
        "\\\"candidate\\\":": true,
        "\\\"person\\\":": true,
        "\\\"name\\\":": true,
        "\\\"given\\\":": true,
        "\\\"Alexander\\\",\\n": true,
        "\\\"family\\\":": true,
        "\\\"Watsford\\\"\\n": true,
        "},\\n": true,
        "\\\"communication\\\":": true,
        "\\\"phone\\\":": true,
        "[\\n": true,
        "\\\"formattedNumber\\\":": true,
        "\\\"(07)": true,
        "1540\\\"\\n": true,
        "],\\n": true,
        "\\\"email\\\":": true,
        "\\\"address\\\":": true,
        "\\\"alexander.watsford@gmail.com\\\"\\n": true,
        "]\\n": true,
        "\\\"associatedPositionProfile\\\":": true,
        "\\\"profileId\\\":": true,
        "\\\"value\\\":": true,
        "\\\"globalPublicTest:positionProfile:unposted:GSg8dZtux2FD8hP4Bmju3B\\\"\\n": true,
        "The": true,
        "hirer": true,
        "associated": true,
        "with": true,
        "the": true,
        "unposted": true,
        "position": true,
        "profile\\n": true,
        "\\\"positionOrganizations\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n": true,
        "\\\"Acme": true,
        "Corp\\\"\\n": true,
        "Current": true,
        "information": true,
        "from": true,
        "candidate’s": true,
        "SEEK": true,
        "Profile\\n": true,
        "\\\"employment\\\":": true,
        "\\\"organization\\\":": true,
        "\\\"Sofa": true,
        "Express\\\"\\n": true,
        "\\\"positionHistories\\\":": true,
        "\\\"start\\\":": true,
        "\\\"2020-06-10T04:58:23.730Z\\\",\\n": true,
        "\\\"end\\\":": true,
        "null,\\n": true,
        "\\\"title\\\":": true,
        "\\\"Store": true,
        "manager\\\",\\n": true,
        "\\\"descriptions\\\":": true,
        "[],\\n": true,
        "\\\"current\\\":": true,
        "true\\n": true,
        "\\\"attachments\\\":": true,
        "`Resume`": true,
        "document\\n": true,
        "\\\"seekRoleCode\\\":": true,
        "\\\"Resume\\\",\\n": true,
        "\\\"url\\\":": true,
        "\\\"https://graphql.seek.com/anzPublicTest/purchasedProfiles/seekAnzPublicTest%3AcandidateProfile%3Apurchased%3A73nRti9c7jBm/attachments/resume\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":167,\"column\":1,\"offset\":5280},\"end\":{\"line\":231,\"column\":4,\"offset\":6862},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($id: String!) {
  candidateProfile(id: $id) {
    candidate {
      person {
        name {
          given
          family
        }
        communication {
          phone {
            formattedNumber
          }
          email {
            address
          }
        }
      }
    }
    associatedPositionProfile {
      profileId {
        value
      }
      positionOrganizations {
        id {
          value
        }
        name
      }
    }
    employment {
      organization {
        name
      }
      positionHistories {
        start
        end
        title
        descriptions
        current
      }
    }
    attachments {
      seekRoleCode
      url
    }
  }
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;